import {
  Configuration,
  TradingAPIApi,
  PositionRequestDTO,
} from "js-metacopier-api";
import axios from "axios";

// Trading symbol constant
export const TRADING_SYMBOL = "XAUUSD";

// Point value in USD (how much 1 point is worth in USD)
const POINT_VALUE_USD = 1;

export interface SymbolInfo {
  name: string;
  points: number;
  digits: number;
  lotSize: number;
}

export interface TradeParams {
  symbol: string;
  action: "BUY" | "SELL";
  lots: number;
  tp: number;
  sl: number;
}

export interface TradeResult {
  success: boolean;
  message: string;
  data?: any;
  slippageCheck?: {
    expectedPrice: number;
    tpUsd: number;
    slUsd: number;
    volume: number;
    isBuy: boolean;
  };
}

export interface QuoteData {
  symbol: string;
  bid: number;
  ask: number;
  timestamp?: number;
}

export interface Position {
  id: string;
  symbol: string;
  type: string;
  volume: number;
  openPrice: number;
  currentPrice: number;
  profit: number;
  netProfit: number;
  takeProfit?: number;
  stopLoss?: number;
  openTime: string;
}

class TradingService {
  private api: TradingAPIApi | null = null;
  private apiKey: string = "";
  private accountId: string = "";
  private symbolInfo: SymbolInfo | null = null;

  connect(apiKey: string, accountId: string): boolean {
    try {
      // Trim and clean the inputs to remove any whitespace or invalid characters
      this.apiKey = apiKey.trim();
      this.accountId = accountId.trim();

      // Initialize the MetaCopier API with configuration
      const config = new Configuration({
        apiKey: this.apiKey,
        basePath: "https://api.metacopier.io",
      });

      // Create TradingAPIApi with config and axios instance
      this.api = new TradingAPIApi(config, "https://api.metacopier.io", axios);

      // Fetch symbol info after connecting
      this.fetchSymbolInfo();

      return true;
    } catch (error) {
      console.error("Failed to connect:", error);
      return false;
    }
  }

  async fetchSymbolInfo(): Promise<void> {
    if (!this.isConnected()) {
      return;
    }

    try {
      const response = await axios.get(
        `https://api.metacopier.io/rest/api/v1/accounts/${this.accountId}/symbols`,
        {
          headers: {
            "X-API-KEY": this.apiKey,
            "Content-Type": "application/json",
          },
        },
      );

      const symbols = response.data || [];
      const symbol = symbols.find((s: any) => s.name === TRADING_SYMBOL);

      if (symbol) {
        this.symbolInfo = {
          name: symbol.name,
          points: symbol.points || 0.01,
          digits: symbol.digits || 2,
          lotSize: symbol.lotSize || 100000,
        };
        console.log("Symbol info loaded:", this.symbolInfo);
      }
    } catch (error: any) {
      console.error("Failed to fetch symbol info:", error);
    }
  }

  isConnected(): boolean {
    return this.api !== null && this.apiKey !== "" && this.accountId !== "";
  }

  /**
   * Convert USD amount to price for TP/SL
   * Formula: price_change = (usd_amount) / (point_value_usd * lot_size * volume)
   */
  convertUsdToPrice(usdAmount: number, volume: number, isBuy: boolean): number {
    if (!this.symbolInfo || usdAmount === 0) {
      return 0;
    }

    // Calculate how many points are needed for the USD amount
    // USD = points * point_value * lot_size * volume
    // points = USD / (point_value * lot_size * volume)
    const points = usdAmount / (POINT_VALUE_USD * volume);

    // Convert points to price change
    const priceChange = points * this.symbolInfo.points;

    return priceChange;
  }

  async executeTrade(
    params: TradeParams,
    quote: QuoteData,
  ): Promise<TradeResult> {
    if (!this.isConnected() || !this.api) {
      return {
        success: false,
        message: "Not connected. Please enter your API key first.",
      };
    }

    try {
      const isBuy = params.action === "BUY";

      // Use ask for buy orders, bid for sell orders
      const currentPrice = isBuy ? quote.ask : quote.bid;

      // Convert USD amounts to price levels
      let takeProfitPrice = 0;
      let stopLossPrice = 0;

      if (params.tp > 0) {
        const tpPriceChange = this.convertUsdToPrice(
          params.tp,
          params.lots,
          isBuy,
        );
        takeProfitPrice = isBuy
          ? currentPrice + tpPriceChange
          : currentPrice - tpPriceChange;
      }

      if (params.sl > 0) {
        const slPriceChange = this.convertUsdToPrice(
          params.sl,
          params.lots,
          isBuy,
        );
        stopLossPrice = isBuy
          ? currentPrice - slPriceChange
          : currentPrice + slPriceChange;
      }

      const positionRequest: PositionRequestDTO = {
        symbol: params.symbol,
        orderType: isBuy ? "Buy" : "Sell",
        volume: params.lots,
        takeProfit: takeProfitPrice,
        stopLoss: stopLossPrice,
        openPrice: 0, // For market orders
        requestId: Math.floor(Math.random() * 1000), // Random request ID 0-999
      };

      console.log("Executing trade with request:", positionRequest);
      console.log(
        `Current price: ${currentPrice}, TP: ${takeProfitPrice}, SL: ${stopLossPrice}`,
      );
      const response = await this.api.openPosition(
        this.accountId,
        positionRequest,
      );
      console.log("Trade response:", response);

      // Store the original TP/SL USD amounts for slippage adjustment
      const tradeInfo = {
        success: true,
        message: `${params.action} order executed successfully!`,
        data: response.data,
        slippageCheck: {
          expectedPrice: currentPrice,
          tpUsd: params.tp,
          slUsd: params.sl,
          volume: params.lots,
          isBuy: isBuy,
        },
      };

      return tradeInfo;
    } catch (error: any) {
      console.error("Trade execution error:", error);
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to execute trade",
      };
    }
  }

  async adjustPositionForSlippage(
    positionId: string,
    actualOpenPrice: number,
    tpUsd: number,
    slUsd: number,
    volume: number,
    isBuy: boolean,
  ): Promise<void> {
    if (!this.isConnected() || !this.api) {
      return;
    }

    try {
      // Recalculate TP/SL based on actual open price
      let newTpPrice = 0;
      let newSlPrice = 0;

      if (tpUsd > 0) {
        const tpPriceChange = this.convertUsdToPrice(tpUsd, volume, isBuy);
        newTpPrice = isBuy
          ? actualOpenPrice + tpPriceChange
          : actualOpenPrice - tpPriceChange;
      }

      if (slUsd > 0) {
        const slPriceChange = this.convertUsdToPrice(slUsd, volume, isBuy);
        newSlPrice = isBuy
          ? actualOpenPrice - slPriceChange
          : actualOpenPrice + slPriceChange;
      }

      console.log(
        `Adjusting position ${positionId} for slippage. New TP: ${newTpPrice}, New SL: ${newSlPrice}`,
      );

      // Modify the position with new TP/SL using direct API call
      // Need to pass all required fields for the modify endpoint
      await axios.put(
        `https://api.metacopier.io/rest/api/v1/accounts/${this.accountId}/positions/${positionId}`,
        {
          symbol: TRADING_SYMBOL,
          orderType: isBuy ? "Buy" : "Sell",
          volume: volume,
          takeProfit: newTpPrice,
          stopLoss: newSlPrice,
          openPrice: actualOpenPrice,
          requestId: Math.floor(Math.random() * 1000),
        },
        {
          headers: {
            "X-API-KEY": this.apiKey,
            "Content-Type": "application/json",
          },
        },
      );

      console.log("Position adjusted successfully for slippage");
    } catch (error: any) {
      console.error("Failed to adjust position for slippage:", error);
    }
  }

  async getQuote(symbol: string): Promise<QuoteData | null> {
    if (!this.isConnected()) {
      return null;
    }

    try {
      // Use direct HTTP request to get quote since it's not in the library
      const response = await axios.get(
        `https://api.metacopier.io/rest/api/v1/accounts/${this.accountId}/quote/${symbol}`,
        {
          headers: {
            "X-API-KEY": this.apiKey,
            "Content-Type": "application/json",
          },
        },
      );

      const quoteData = response.data;

      return {
        symbol: quoteData.symbol || symbol,
        bid: quoteData.bid || 0,
        ask: quoteData.ask || 0,
        timestamp: Date.now(),
      };
    } catch (error: any) {
      console.error("Failed to fetch quote:", error);
      return null;
    }
  }

  async getOpenPositions(): Promise<Position[]> {
    if (!this.isConnected()) {
      return [];
    }

    try {
      const response = await axios.get(
        `https://api.metacopier.io/rest/api/v1/accounts/${this.accountId}/positions`,
        {
          headers: {
            "X-API-KEY": this.apiKey,
            "Content-Type": "application/json",
          },
        },
      );

      const positions = response.data || [];
      return positions.map((pos: any) => ({
        id: pos.id || pos.positionId || "",
        symbol: pos.symbol || "",
        type: pos.type || pos.orderType || "",
        volume: pos.volume || 0,
        openPrice: pos.openPrice || pos.price || 0,
        currentPrice: pos.currentPrice || pos.price || 0,
        profit: pos.profit || pos.unrealizedProfit || 0,
        netProfit: pos.netProfit || pos.profit || pos.unrealizedProfit || 0,
        takeProfit: pos.takeProfit,
        stopLoss: pos.stopLoss,
        openTime: pos.openTime || pos.time || new Date().toISOString(),
      }));
    } catch (error: any) {
      console.error("Failed to fetch positions:", error);
      return [];
    }
  }

  async closePosition(positionId: string): Promise<TradeResult> {
    if (!this.isConnected() || !this.api) {
      return {
        success: false,
        message: "Not connected. Please enter your API key first.",
      };
    }

    try {
      await this.api.closePosition(this.accountId, positionId);

      return {
        success: true,
        message: "Position closed successfully!",
      };
    } catch (error: any) {
      console.error("Failed to close position:", error);
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to close position",
      };
    }
  }

  disconnect(): void {
    this.api = null;
    this.apiKey = "";
    this.accountId = "";
    this.symbolInfo = null;
  }
}

const tradingService = new TradingService();
export default tradingService;
